import { GridFilterItem } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  LabelValue,
  DEFAULT_FILTER_ITEMS,
  buildColumnDefs,
  buildFilterModel,
  buildStatusOptions,
  buildStatusGroups,
  buildJobTypeOptions,
} from './servicesV4Filters';
import { ServiceListDataType } from '../../@types/service.type';
import { ServiceQueryOptions } from '../../api/services';
import { FilterState, OptionGroup } from '../../components/molecules/FilterBar';
import { ColumnDef } from '../../components/organisms/ServiceTable';
import { ColumnSelectionItem } from '../../components/templates/ColumnSelection';
import { columnSelectionItems, SERVICE_DEFAULT_REPORT_TYPES } from '../../constants/service.constant';
import { useGetServicePaginatedData, useGetServiceActionProcessTypes } from '../../queries/service-query';
import { useGetServicesStatuses } from '../../queries/service-status-query';

// Re-export for consumers
export { filterModelToFilterState } from './servicesV4Filters';
export type { LabelValue };

interface SortState {
  field: string;
  sort: string | null | undefined;
}

export interface ServicesV4DataReturn {
  data: ServiceListDataType[];
  isLoading: boolean;
  isFetching: boolean;
  page: number;
  pageSize: number;
  total: number;
  fetchDurationMs: number | null;
  sortModel: SortState;
  columns: ColumnDef[];
  columnSelectionData: ColumnSelectionItem[];
  statusOptions: LabelValue[];
  statusGroups: OptionGroup[];
  jobTypeOptions: LabelValue[];
  setPage: (p: number) => void;
  setPageSize: (s: number) => void;
  setSortModel: (m: SortState) => void;
  filterModel: { items: GridFilterItem[] };
  applyFilters: (filters: FilterState) => void;
  applyReportConfig: (filterModel: { items: GridFilterItem[] }, colSelection?: ColumnSelectionItem[]) => void;
  updateColumnSelection: (data: ColumnSelectionItem[]) => void;
  refetch: () => void;
  multiSearchQuery: string;
}

export const useServicesV4Data = (): ServicesV4DataReturn => {
  const location = useLocation();

  // Derive defaultReport from URL path (e.g. /services/myServices → "myServices")
  const defaultReport = useMemo(
    () => SERVICE_DEFAULT_REPORT_TYPES.find((r) => location.pathname.includes(r)),
    [location.pathname],
  );

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortModel, setSortModel] = useState<SortState>({
    field: 'serviceId',
    sort: 'desc',
  });
  const [filterModel, setFilterModel] = useState<{ items: GridFilterItem[] }>({
    // When a default report is active, the BFF handles filtering — send empty items
    items: defaultReport ? [] : DEFAULT_FILTER_ITEMS,
  });
  const [multiSearchQuery, setMultiSearchQuery] = useState('');
  const [columns, setColumns] = useState<ColumnDef[]>(() => buildColumnDefs());
  const [colSelectionData, setColSelectionData] = useState<ColumnSelectionItem[]>(
    columnSelectionItems as unknown as ColumnSelectionItem[],
  );
  const [fetchDurationMs, setFetchDurationMs] = useState<number | null>(null);
  const fetchStartRef = useRef<number | null>(null);
  const prevDataRef = useRef<ServiceListDataType[]>([]);
  const prevTotalRef = useRef<number>(0);

  // Fetch filter dropdown options
  const statusesQuery = useGetServicesStatuses();
  const typesQuery = useGetServiceActionProcessTypes();

  // Trigger initial status fetch
  useEffect(() => {
    statusesQuery.refetch();
  }, []);

  const statusOptions = useMemo<LabelValue[]>(
    () => buildStatusOptions(statusesQuery.data),
    [statusesQuery.data],
  );

  const statusGroups = useMemo<OptionGroup[]>(
    () => buildStatusGroups(statusesQuery.data),
    [statusesQuery.data],
  );

  const jobTypeOptions = useMemo<LabelValue[]>(
    () => buildJobTypeOptions(typesQuery.data),
    [typesQuery.data],
  );

  const queryOptions: ServiceQueryOptions = useMemo(() => ({
    filterModel,
    page,
    pageSize,
    sortModel: [sortModel],
    multiSearchQuery: multiSearchQuery || undefined,
    defaultReport,
  }), [filterModel, page, pageSize, sortModel, multiSearchQuery, defaultReport]);

  const colSelection = useMemo(
    () => columnSelectionItems as unknown as ColumnSelectionItem[],
    [],
  );

  const query = useGetServicePaginatedData(queryOptions, colSelection);

  // Trigger initial fetch and refetch on query option changes
  useEffect(() => {
    fetchStartRef.current = performance.now();
    query.refetch();
  }, [queryOptions]);

  // Track fetch duration when query settles
  useEffect(() => {
    if (!query.isFetching && fetchStartRef.current !== null) {
      setFetchDurationMs(performance.now() - fetchStartRef.current);
      fetchStartRef.current = null;
    }
  }, [query.isFetching]);

  const applyFilters = useCallback((filters: FilterState) => {
    setMultiSearchQuery(filters.multiSearch || '');
    setFilterModel(buildFilterModel(filters));
    setPage(0);
  }, []);

  const updateColumnSelection = useCallback((data: ColumnSelectionItem[]) => {
    setColSelectionData(data);
    const visibleFields = new Set<string>();
    for (const group of data) {
      for (const col of group.value) {
        if (col.value) visibleFields.add(col.field);
      }
    }
    setColumns((prev) =>
      prev.map((c) => ({ ...c, visible: visibleFields.has(c.key) })),
    );
  }, []);

  const applyReportConfig = useCallback((
    reportFilterModel: { items: GridFilterItem[] },
    colSelection?: ColumnSelectionItem[],
  ) => {
    setFilterModel(reportFilterModel);
    setMultiSearchQuery('');
    setPage(0);
    if (colSelection) {
      updateColumnSelection(colSelection);
    }
  }, [updateColumnSelection]);

  // Keep previous data visible during refetch
  useEffect(() => {
    if (query.data?.data) {
      prevDataRef.current = query.data.data;
      prevTotalRef.current = query.data.total ?? 0;
    }
  }, [query.data]);

  const refetch = useCallback(() => {
    query.refetch();
  }, [query]);

  const currentData = query.data?.data ?? prevDataRef.current;
  const currentTotal = query.data?.total ?? prevTotalRef.current;

  return {
    data: currentData,
    isLoading: query.isLoading && prevDataRef.current.length === 0,
    isFetching: query.isFetching,
    page,
    pageSize,
    total: currentTotal,
    fetchDurationMs,
    sortModel,
    columns,
    columnSelectionData: colSelectionData,
    filterModel,
    statusOptions,
    statusGroups,
    jobTypeOptions,
    setPage,
    setPageSize,
    setSortModel,
    applyFilters,
    applyReportConfig,
    updateColumnSelection,
    refetch,
    multiSearchQuery,
  };
};
